// Custom DS-Table column for Win Probability: the exact percentage (e.g. "95 %", matching the project's
// Settings → Status "Win Probability" field), tinted in the win-probability band colour — no dot. The
// public column API cannot express a coloured value cell, so we author a column (dist-internal imports are
// the accepted web-front convention for custom DS columns — see ContactColumn / StatusPillColumn).
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import Styled from 'styled-components';
import type {OpportunitySortingFn, OpportunityTableRow} from '../opportunityTableRows';
import {winProbabilityColour} from '../winProbabilityCircle';

export interface WinProbabilityColumnType {
	id?: string;
	accessorKey: string;
	header: string;
	visible?: boolean;
	align?: Align;
	'data-cy'?: string;
	allowSorting?: boolean;
	sortingFn?: OpportunitySortingFn;
	// Placement for rows whose sorted value (accessor) is `undefined` — DS Table passes this straight to the
	// tanstack column so unset rows can be pinned last regardless of sort direction.
	sortUndefined?: 'first' | 'last';
	width?: string;
}

const Label = Styled.span``;

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const WinProbabilityColumn = (_: WinProbabilityColumnType): null => null;

WinProbabilityColumn.toColumnDef = (child: React.ReactElement<WinProbabilityColumnType>): ColumnDef<OpportunityTableRow> => {
	const {align} = child.props;
	return {
		...defaultColumnDef(child.props),
		accessorKey: child.props.accessorKey,
		sortingFn: child.props.sortingFn,
		sortUndefined: child.props.sortUndefined,
		cell: ({row}: {row: {original: OpportunityTableRow}}) => {
			const colour = winProbabilityColour(row.original.winChance);
			return (
				<StyledCell align={align} data-cy="opportunity-win-probability-cell">
					{/* Exact percentage (e.g. "95 %") matching the Settings → Status Win Probability field, tinted
					    in the band colour. No dot. */}
					<Label style={{color: colour}}>{`${Math.round(row.original.winChance * 100)} %`}</Label>
				</StyledCell>
			);
		},
	};
};

export default WinProbabilityColumn;
